import React, { useState } from 'react';
import { getFileDisplayName, formatFileSize, getFileExtension } from '../../core/utils/fileUtils';

const FileEmbedContent = ({ file, height }) => {
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [error, setError] = useState(null);
  const [embedImageLoaded, setEmbedImageLoaded] = useState(false);

  if (!file) return null;

  // Prefer the stored file_type, then derive from key/url (query params stripped inside getFileExtension)
  const fileExtension = file.file_type || getFileExtension(file.file_s3_key || file.file_url || file.file_name || '');
  const displayName = getFileDisplayName(file);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.file_url;
    link.download = file.download_name || displayName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetImageTransform = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImageWheel = (e) => {
    e.preventDefault();
    const zoomFactor = 0.1;
    const newZoom = e.deltaY > 0 ? Math.max(0.5, imageZoom - zoomFactor) : Math.min(3, imageZoom + zoomFactor);
    setImageZoom(newZoom);
  };

  const handleImageMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
  };

  const handleImageMouseMove = (e) => {
    if (isDragging) {
      setImagePosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  const containerStyle = height ? { height: typeof height === 'number' ? `${height}px` : height } : {};
  const extension = fileExtension.toLowerCase();

  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return (
      <div
        className="w-full overflow-hidden relative flex items-center justify-center"
        style={containerStyle}
        onWheel={handleImageWheel}
        onMouseMove={handleImageMouseMove}
        onMouseUp={handleImageMouseUp}
        onMouseLeave={handleImageMouseUp}
      >
        <img
          src={file.file_url}
          alt={displayName}
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `scale(${imageZoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out, opacity 0.35s ease-in-out',
            cursor: imageZoom > 1 ? 'move' : 'zoom-in',
            opacity: embedImageLoaded ? 1 : 0,
          }}
          onMouseDown={handleImageMouseDown}
          onLoad={() => setEmbedImageLoaded(true)}
          onError={() => { setEmbedImageLoaded(true); setError('Erro ao carregar imagem'); }}
          draggable={false}
        />
        {file.file_url && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(200, 200, 200, 0.15)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              borderRadius: "inherit",
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 1,
              opacity: embedImageLoaded ? 0 : 1,
              transition: "opacity 0.3s ease-out",
            }}
            aria-hidden="true"
          >
            <span
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
                animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
              }}
            />
          </div>
        )}
        {imageZoom !== 1 && (
          <button
            onClick={resetImageTransform}
            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-md hover:bg-opacity-70 transition-all"
            title="Resetar zoom"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // PDF
  if (extension === 'pdf') {
    return (
      <div className="w-full" style={containerStyle}>
        <iframe
          src={`${file.file_url}#toolbar=1&navpanes=1&scrollbar=1`}
          className="w-full h-full border-0"
          title={displayName}
          onError={() => setError('Erro ao carregar PDF')}
        />
      </div>
    );
  }

  // Text-like
  if (['txt', 'md', 'json', 'xml', 'csv', 'log'].includes(extension)) {
    return (
      <div className="w-full bg-gray-50 overflow-auto" style={containerStyle}>
        <iframe
          src={file.file_url}
          className="w-full h-full border-0 bg-white"
          title={displayName}
          onError={() => setError('Erro ao carregar arquivo de texto')}
        />
      </div>
    );
  }

  // Audio
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension)) {
    return (
      <div className="w-full flex flex-col items-center justify-center p-4" style={containerStyle}>
        <div className="text-5xl mb-2">🎵</div>
        <audio controls className="w-full max-w-md">
          <source src={file.file_url} type={`audio/${extension === 'mp3' ? 'mpeg' : extension}`} />
          Seu navegador não suporta reprodução de áudio.
        </audio>
      </div>
    );
  }

  // Video
  if (['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(extension)) {
    return (
      <div className="w-full flex items-center justify-center" style={containerStyle}>
        <video controls className="max-w-full max-h-full w-full">
          <source src={file.file_url} type={`video/${extension}`} />
          Seu navegador não suporta reprodução de vídeo.
        </video>
      </div>
    );
  }

  // Office via Google Docs Viewer
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(file.file_url)}&embedded=true`;
    return (
      <div className="w-full" style={containerStyle}>
        <iframe
          src={viewerUrl}
          className="w-full h-full border-0"
          title={displayName}
          onError={() => setError('Erro ao carregar documento. Arquivo pode não estar acessível publicamente.')}
        />
      </div>
    );
  }

  // Code-like fallback in iframe
  if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'go', 'rs', 'sh'].includes(extension)) {
    return (
      <div className="w-full bg-gray-50 overflow-auto" style={containerStyle}>
        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm h-full">
          <iframe
            src={file.file_url}
            className="w-full h-full border-0 bg-transparent text-green-400"
            title={displayName}
            onError={() => setError('Erro ao carregar código')}
          />
        </div>
      </div>
    );
  }

  // Default fallback with info and download
  return (
    <div className="w-full flex flex-col items-center justify-center p-6 text-center" style={containerStyle}>
      <div className="text-6xl mb-4">📄</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{displayName}</h3>
      <p className="text-gray-600 mb-4">Preview não disponível</p>
      <p className="text-sm text-gray-500 mb-6">
        {file.description && (
          <span className="block mb-2">{file.description}</span>
        )}
        {file.file_size && (
          <span>Tamanho: {formatFileSize(file.file_size)}</span>
        )}
      </p>
      <button
        onClick={handleDownload}
        className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Baixar Arquivo
      </button>
    </div>
  );
};

export default FileEmbedContent;


